import React, { useState } from "react";
import "./Careers.css";
import { Row, Col, Container } from "react-bootstrap";
import { useLanguage } from "../../Context/LanguageContext";
import { useQuery } from "@tanstack/react-query";
import JobCard from "../../components/JobCard/JobCard";
import corner1 from "../../assets/utility_images/corner1.svg";
import corner2 from "../../assets/utility_images/corner2.svg";
import quotes from "../../assets/utility_images/quotation.svg";
import { Link as ScrollLink } from "react-scroll";
import JobApplicationModal from "../../components/JobApplicationModal/JobApplicationModal";
import { Link } from "react-router-dom";
import Loader from "../../components/Loader/Loader";
import fallbackDataCareersEN from "../../FallbackAPI/CareersLocal/FallbackApiCareersEN.json";
import fallbackDataCareersKR from "../../FallbackAPI/CareersLocal/FallbackApiCareersKR.json";
import arrowRight from "../../assets/utility_images/arrow-right.svg";
import arrowRightWhite from "../../assets/utility_images/arrow-right-white.svg";

// Mapping localized titles to English for API compatibility
const titleMapping = {
  "백엔드 엔지니어": "Backend Engineer",
  "AI/ML 엔지니어": "AI/ML Engineer",
  "컴퓨터 비전/알고리즘 개발 엔지니어": "Computer Vision Engineer",
  "UI/시스템 애플리케이션 엔지니어": "UI System Engineer",
  "상호 운용성 엔지니어": "Interop Engineer",
  // Add other mappings here
};

// Strip JD prefix, trailing parentheticals, normalize whitespace
const normalize = (s) =>
  s
    .normalize("NFC")
    .replace(/^JD\d+:\s*/i, "")
    .replace(/\s*\(.*?\)\s*/g, "")
    .replace(/\s+/g, " ")
    .trim();

const getEnglishTitle = (localizedTitle) => {
  const cleanTitle = normalize(localizedTitle);
  return titleMapping[cleanTitle] || cleanTitle;
};

const routeSlugFor = (englishTitle) => {
  const STOPWORDS = new Set(["engineer", "developer", "dev"]);

  const tokens = englishTitle
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((t) => t && !STOPWORDS.has(t));

  if (tokens.length === 0) return "jobs";

  // Fuse very short first tokens (e.g. "ui", "ai") with the next one
  if (tokens[0].length <= 2 && tokens[1]) {
    return tokens[0] + tokens[1];
  }
  return tokens[0];
};

const Careers = () => {
  const [show, setShow] = useState(false);

  const handleClose = () => {
    setShow(false);
  };

  const handleShow = (job) => {
    setShow(true);
  };

  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const { language } = useLanguage();

  const fetchCareersData = async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 1500); // 1.5s timeout

    try {
      const response = await fetch(`${baseUrl}/page/Careers/${language}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        signal: controller.signal,
      });

      clearTimeout(timeout);
      if (!response.ok) {
        throw new Error("Failed to fetch careers data");
      }
      return response.json();
    } catch (error) {
      console.warn("API request failed, using fallback data.");
      return language === "en" ? fallbackDataCareersEN : fallbackDataCareersKR;
    }
  };

  const {
    data: CareersApiData,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["CareersData", language],
    queryFn: fetchCareersData,
    staleTime: 10 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <div>Error loading data.</div>;
  }

  if (!CareersApiData?.isEnabled) {
    return null;
  }

  const renderSection = (section) => {
    switch (section.sectionName) {
      case "JobSection":
        return (
          <Container fluid style={{ paddingLeft: 0, paddingRight: 0 }}>
            <section className="job-section" id="jobs">
              <div className="job">
                <div className="corner-1">
                  <img src={corner1} alt="" loading="lazy" />
                </div>
                <div className="heading-container d-flex flex-column align-items-start">
                  <div className="section-heading d-flex align-items-center">
                    <div className="line"></div>
                    <div className="section-title">{section.heading}</div>
                  </div>
                  <div className="pillar-header">{section.introText}</div>
                </div>
                <div className="job-container">
                  <Row xs={1} sm={1} md={2} lg={2} xl={3}>
                    {section.sectionItems
                      .filter((item) => item.isEnabled)
                      .sort((a, b) => a.order - b.order)
                      .map((item) => (
                        <Col className="pb-4" key={item.id}>
                          <Link
                            className="linkToPage"
                            to={`/jobs/${routeSlugFor(
                              getEnglishTitle(item.title)
                            )}`}
                            target="_blank"
                          >
                            <JobCard heading={item.title} />
                          </Link>
                        </Col>
                      ))}
                  </Row>
                </div>
                <div className="corner-2">
                  <img src={corner2} alt="" loading="lazy" />
                </div>
              </div>
            </section>
          </Container>
        );

      case "QuoteSection":
        return (
          <Container fluid style={{ paddingLeft: 0, paddingRight: 0 }}>
            <section className="quote-section">
              <div className="quote d-flex justify-content-between">
                <div className="quote-img">
                  <img src={quotes} alt="" loading="lazy" />
                </div>
                <div className="quote-container d-flex flex-column align-items-start">
                  <div className="statement">{section.heading}</div>
                  <div className="author-name">{section.introText}</div>
                </div>
              </div>
            </section>
          </Container>
        );

      case "FormSection":
        return (
          <Container fluid style={{ paddingLeft: 0, paddingRight: 0 }}>
            <section className="form-section">
              <div className="form">
                <div className="form-section-content d-flex flex-column align-items-center">
                  <div className="heading">{section.heading}</div>
                  <div className="form-submission ">
                    {section.introText}
                    <button className="job-form-button" onClick={handleShow}>
                      <span>{section.buttonText}</span>
                    </button>
                    &nbsp;
                    {section.description}
                  </div>
                </div>
              </div>
            </section>
            <JobApplicationModal show={show} handleClose={handleClose} />
          </Container>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {CareersApiData.viewPort.isEnabled && (
        <Container fluid style={{ paddingLeft: 0, paddingRight: 0 }}>
          <section className="Hero">
            <div className="hero-section">
              <div className="hero-banner d-flex">
                <div className="background-hero d-flex justify-content-start align-items-center">
                  <img
                    src={CareersApiData.viewPort.illustrationUrl}
                    alt="About Us"
                    loading="lazy"
                  />
                  <div className="hero-description d-flex flex-column">
                    <div className="careers-heading">
                      {CareersApiData.viewPort.heading
                        .split(",")
                        .map((part, index) => (
                          <React.Fragment key={index}>
                            <span>{part.trim()}</span>
                            {index !==
                              CareersApiData.viewPort.heading.split(",")
                                .length -
                                1 && <br />}
                          </React.Fragment>
                        ))}
                    </div>
                    <div className="careers-paragraph">
                      <p>{CareersApiData.viewPort.description}</p>
                    </div>
                    <ScrollLink
                      to="jobs"
                      smooth={true}
                      duration={200}
                      offset={-50}
                      className="linkToPage"
                    >
                      <button className="btn-hero d-flex justify-content-evenly align-items-center">
                        {CareersApiData.viewPort.buttonText}
                        <div className="hero-btn">
                          <img
                            className="hover-arrow"
                            src={arrowRight}
                            alt=""
                            loading="lazy"
                          />
                          <img
                            className="hover-arrow-white"
                            src={arrowRightWhite}
                            alt=""
                            loading="lazy"
                          />
                        </div>
                      </button>
                    </ScrollLink>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </Container>
      )}

      {CareersApiData.sections
        .filter((section) => section.isEnabled)
        .sort((a, b) => a.sectionOrder - b.sectionOrder)
        .map((section) => renderSection(section))}
    </div>
  );
};

export default Careers;